<script setup lang="ts">
import type { CreateMaintenanceScheduleInput } from '../internal/types.ts'
import { parseLocalDate } from '@taler/domain'
import { ActionButton, StatusTag } from '@taler/ui'
import { computed, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'

const props = defineProps<{ isSubmitting: boolean; errorMessage: string | undefined }>()
const emit = defineEmits<{ create: [CreateMaintenanceScheduleInput] }>()

const { t } = useI18n()
const open = ref(false)
const name = ref('')
const intervalKm = ref('')
const intervalMonths = ref('')
const lastDoneDate = ref('')
const lastDoneMileageKm = ref('')
const validationError = ref<string | undefined>(undefined)

function reset(): void {
  name.value = ''
  intervalKm.value = ''
  intervalMonths.value = ''
  lastDoneDate.value = ''
  lastDoneMileageKm.value = ''
  validationError.value = undefined
}

const intervalMissing = computed(() => intervalKm.value === '' && intervalMonths.value === '')

function submit(): void {
  validationError.value = undefined
  if (name.value.trim() === '') return
  if (intervalMissing.value) {
    validationError.value = t('garage.schedules.intervalRequired')
    return
  }
  const input: CreateMaintenanceScheduleInput = {
    name: name.value.trim(),
    ...(intervalKm.value === '' ? {} : { intervalKm: parseInt(intervalKm.value, 10) }),
    ...(intervalMonths.value === '' ? {} : { intervalMonths: parseInt(intervalMonths.value, 10) }),
    ...(lastDoneDate.value === '' ? {} : { lastDoneDate: parseLocalDate(lastDoneDate.value) }),
    ...(lastDoneMileageKm.value === '' ? {} : { lastDoneMileageKm: parseInt(lastDoneMileageKm.value, 10) }),
  }
  emit('create', input)
}

watch(
  () => props.isSubmitting,
  (submitting, wasSubmitting) => {
    if (wasSubmitting && !submitting && props.errorMessage === undefined) {
      reset()
      open.value = false
    }
  },
)

const combinedError = computed(() => validationError.value ?? props.errorMessage)
</script>

<template>
  <div>
    <ActionButton v-if="!open" type="button" @click="open = true">
      {{ t('garage.schedules.add') }}
    </ActionButton>

    <form
      v-else
      class="flex flex-col gap-3 rounded-[var(--radius-surface)] border border-[color:var(--color-border)] p-4 bg-[color:var(--color-surface)]"
      @submit.prevent="submit"
    >
      <label class="flex flex-col gap-1">
        <span class="text-[length:var(--text-step-2)] font-medium text-[color:var(--color-text)]">{{
          t('garage.schedules.nameLabel')
        }}</span>
        <input
          v-model="name"
          type="text"
          required
          :placeholder="t('garage.schedules.namePlaceholder')"
          class="h-[var(--density-control)] rounded-[var(--radius-control)] border border-[color:var(--color-border-control)] bg-[color:var(--color-surface)] px-[var(--space-cell)] text-[color:var(--color-text)]"
        />
      </label>

      <fieldset class="flex flex-col gap-2">
        <legend class="text-[length:var(--text-step-2)] font-medium text-[color:var(--color-text)]">
          {{ t('garage.schedules.intervalCaption') }}
          <span class="font-normal text-[color:var(--color-text-muted)]"
            >({{ t('garage.schedules.intervalHint') }})</span
          >
        </legend>
        <div class="grid grid-cols-2 gap-[var(--space-cell)]">
          <label class="flex flex-col gap-1">
            <span class="text-[length:var(--text-step-1)] text-[color:var(--color-text-muted)]">{{
              t('garage.schedules.intervalKm')
            }}</span>
            <input
              v-model="intervalKm"
              type="number"
              min="1"
              :placeholder="t('garage.schedules.intervalKmPlaceholder')"
              class="h-[var(--density-control)] rounded-[var(--radius-control)] border border-[color:var(--color-border-control)] bg-[color:var(--color-surface)] px-[var(--space-cell)] text-[color:var(--color-text)]"
            />
          </label>
          <label class="flex flex-col gap-1">
            <span class="text-[length:var(--text-step-1)] text-[color:var(--color-text-muted)]">{{
              t('garage.schedules.intervalMonths')
            }}</span>
            <input
              v-model="intervalMonths"
              type="number"
              min="1"
              :placeholder="t('garage.schedules.intervalMonthsPlaceholder')"
              class="h-[var(--density-control)] rounded-[var(--radius-control)] border border-[color:var(--color-border-control)] bg-[color:var(--color-surface)] px-[var(--space-cell)] text-[color:var(--color-text)]"
            />
          </label>
        </div>
      </fieldset>

      <div class="grid grid-cols-2 gap-[var(--space-cell)]">
        <label class="flex flex-col gap-1">
          <span class="text-[length:var(--text-step-2)] font-medium text-[color:var(--color-text)]">{{
            t('garage.schedules.doneDate')
          }}</span>
          <input
            v-model="lastDoneDate"
            type="date"
            class="h-[var(--density-control)] rounded-[var(--radius-control)] border border-[color:var(--color-border-control)] bg-[color:var(--color-surface)] px-[var(--space-cell)] text-[color:var(--color-text)]"
          />
        </label>
        <label class="flex flex-col gap-1">
          <span class="text-[length:var(--text-step-2)] font-medium text-[color:var(--color-text)]">{{
            t('garage.schedules.doneMileage')
          }}</span>
          <input
            v-model="lastDoneMileageKm"
            type="number"
            min="0"
            :placeholder="t('garage.schedules.doneMileagePlaceholder')"
            class="h-[var(--density-control)] rounded-[var(--radius-control)] border border-[color:var(--color-border-control)] bg-[color:var(--color-surface)] px-[var(--space-cell)] text-[color:var(--color-text)]"
          />
        </label>
      </div>

      <StatusTag v-if="combinedError" tone="danger">{{ combinedError }}</StatusTag>

      <div class="flex gap-2">
        <ActionButton type="submit" :disabled="isSubmitting || intervalMissing">
          {{ t('garage.schedules.create') }}
        </ActionButton>
        <button
          type="button"
          class="min-h-[var(--density-target-min)] rounded-[var(--radius-control)] border border-[color:var(--color-border-control)] px-3 py-2 text-[color:var(--color-text)]"
          @click="open = false"
        >
          {{ t('garage.schedules.cancel') }}
        </button>
      </div>
    </form>
  </div>
</template>
